import { FunctionComponent } from 'react'
import { WChip, WSwitch, WTitle } from '@alice-health/wonderland-react'
import { motion, useReducedMotion } from 'framer-motion'
import classNames from 'classnames'
import { useFilters } from '@/modules/accredited-establishments/hooks'
import { ProviderNameSearchField } from '@/modules/accredited-establishments/components/Filters'
import { MapPrivacyNotice } from '@/modules/accredited-establishments/components/MapPrivacyNotice'
import {
  SEARCH_COLLAPSED_WIDTH,
  SEARCH_EXPANDED_WIDTH,
  SEARCH_CONTROL_HEIGHT,
  SEARCH_TRANSITION_DURATION
} from './PageTitle.constants'

const PageTitle: FunctionComponent = () => {
  const {
    isMapView,
    setView,
    providerName,
    isProviderSearchOpen,
    setProviderSearchOpen
  } = useFilters()

  /* Um termo aplicado mantem o campo aberto mesmo sem o clique na lupa. */
  const isSearchExpanded = isProviderSearchOpen || Boolean(providerName)

  const prefersReducedMotion = useReducedMotion()

  const width = isSearchExpanded
    ? SEARCH_EXPANDED_WIDTH
    : SEARCH_COLLAPSED_WIDTH

  return (
    <div
      className={classNames(
        'page-title-theme',
        'flex items-center justify-between gap-gl-spacing-08',
        // No 56px spacing token, and the Tailwind scale runs on a 14px `rem`.
        'w-full pt-gl-spacing-12 pb-gl-spacing-08 lg:py-[56px]'
      )}
    >
      <WTitle
        className='page-title-type'
        level={1}
        // `large` is 24px below the `md` stop and 32px from it on.
        size={{ xs: 'large', md: 'large' }}
        mode='active'
      >
        Rede credenciada
      </WTitle>

      {/* On mobile the map and the search are the floating pair at the bottom. */}
      <div className='hidden lg:flex items-center gap-gl-spacing-08'>
        <div className='flex items-center gap-gl-spacing-02'>
          {/* `reverse` puts the label before the control. */}
          <WSwitch
            label='Visualizar mapa'
            size='small'
            reverse
            checked={isMapView}
            onWChange={event => setView(event.detail ? 'map' : 'list')}
          />

          <MapPrivacyNotice position='bottom' />
        </div>

        {/* The chip becomes the field: the box grows left and clips the content. */}
        <motion.div
          className='relative shrink-0 overflow-hidden'
          style={{ height: SEARCH_CONTROL_HEIGHT }}
          initial={false}
          animate={{ width }}
          transition={{
            duration: prefersReducedMotion ? 0 : SEARCH_TRANSITION_DURATION,
            ease: 'easeInOut'
          }}
        >
          {/* Anchored right: anchoring left would clip the side the chip sits on. */}
          <div
            className='absolute right-0 top-0 flex justify-end'
            style={{ width: SEARCH_EXPANDED_WIDTH }}
          >
            {isSearchExpanded ? (
              <div className='grow'>
                <ProviderNameSearchField
                  onEmptyBlur={() => setProviderSearchOpen(false)}
                />
              </div>
            ) : (
              // Without `label`, `WChip` renders the icon-only variant.
              <WChip
                leadingIcon='icSearch'
                onWClick={() => setProviderSearchOpen(true)}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export { PageTitle }
